import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';
import {
    MAPS_PARTNER_COMPANIES,
    MAPS_PARTNER_COMPANY_CREATE,
    MAPS_PARTNER_LINK_CREATE,
    MAPS_PARTNER_LINK_RENAME,
    MAPS_PARTNER_LINK_HIDE,
    MAPS_PARTNER_LINK_REVOKE,
    MAPS_PARTNER_LINK_ROTATE,
} from './maps-graphql/queries';
import type {
    MapsPartnerCompany,
    MapsPartnerLinkCreateInput,
    MapsPartnerLinkCreateResult,
} from '../models/maps.types';

/**
 * Thin wrapper around the admin-gated partner-links operations (partner-account
 * model): list companies with their magic-link credentials, create companies,
 * mint links, revoke links. Sibling of AdminService.
 */
@Injectable({ providedIn: 'root' })
export class PartnerLinksService {
    constructor(private readonly apollo: Apollo) {}

    async Companies(): Promise<MapsPartnerCompany[]> {
        const result = await firstValueFrom(
            this.apollo.query<{ mapsPartnerCompanies: MapsPartnerCompany[] }>({
                query: MAPS_PARTNER_COMPANIES,
                fetchPolicy: 'network-only',
            })
        );
        return result.data?.mapsPartnerCompanies ?? [];
    }

    async CreateCompany(name: string): Promise<MapsPartnerCompany> {
        const result = await firstValueFrom(
            this.apollo.mutate<{ mapsPartnerCompanyCreate: MapsPartnerCompany }>({
                mutation: MAPS_PARTNER_COMPANY_CREATE,
                variables: { name },
            })
        );
        const company = result.data?.mapsPartnerCompanyCreate;
        if (!company) throw new Error('Company creation returned no data');
        return company;
    }

    async CreateLink(input: MapsPartnerLinkCreateInput): Promise<MapsPartnerLinkCreateResult> {
        const result = await firstValueFrom(
            this.apollo.mutate<{ mapsPartnerLinkCreate: MapsPartnerLinkCreateResult }>({
                mutation: MAPS_PARTNER_LINK_CREATE,
                variables: { input },
            })
        );
        const link = result.data?.mapsPartnerLinkCreate;
        if (!link) throw new Error('Link creation returned no data');
        return link;
    }

    async RevokeLink(inviteId: string): Promise<boolean> {
        const result = await firstValueFrom(
            this.apollo.mutate<{ mapsPartnerLinkRevoke: { success: boolean } }>({
                mutation: MAPS_PARTNER_LINK_REVOKE,
                variables: { inviteId },
            })
        );
        return result.data?.mapsPartnerLinkRevoke.success ?? false;
    }

    /** Remove a REVOKED link from the list. Hide-only — nothing is deleted
     *  from the database (the id joins a server-side hidden registry). */
    async HideLink(inviteId: string): Promise<boolean> {
        const result = await firstValueFrom(
            this.apollo.mutate<{ mapsPartnerLinkHide: { success: boolean } }>({
                mutation: MAPS_PARTNER_LINK_HIDE,
                variables: { inviteId },
            })
        );
        return result.data?.mapsPartnerLinkHide.success ?? false;
    }

    async RenameLink(inviteId: string, name: string): Promise<boolean> {
        const result = await firstValueFrom(
            this.apollo.mutate<{ mapsPartnerLinkRename: { success: boolean } }>({
                mutation: MAPS_PARTNER_LINK_RENAME,
                variables: { inviteId, name },
            })
        );
        return result.data?.mapsPartnerLinkRename.success ?? false;
    }

    /** Immediate cut-over: mints the successor, revokes the old link. */
    async RotateLink(inviteId: string): Promise<MapsPartnerLinkCreateResult> {
        const result = await firstValueFrom(
            this.apollo.mutate<{ mapsPartnerLinkRotate: MapsPartnerLinkCreateResult }>({
                mutation: MAPS_PARTNER_LINK_ROTATE,
                variables: { inviteId },
            })
        );
        const link = result.data?.mapsPartnerLinkRotate;
        if (!link) throw new Error('Link rotation returned no data');
        return link;
    }
}
